import { I18nextContext, useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import menu, { ICategory } from "./menuStructure"
import { SortDirection, useIsAlphabetical, useSearchQuery, useSortDirection } from "../side-menu-store"
import { useContext, useMemo } from "react"
import { TFunction } from "gatsby-plugin-react-i18next"
import { ISubCategory } from "../sub-elements/SubCategory"

type DirectionFactor = -1 | 0 | 1

const compare = (a: string, b: string, languageCode: string) =>
  a.localeCompare(b, languageCode, { sensitivity: 'accent' })

const areEquivalent = (a: string, b: string, languageCode: string): boolean =>
  a.localeCompare(b, languageCode, { sensitivity: 'base' }) === 0

export interface IDisplayedCategory extends ICategory {
  hideOnAlphabetical?: boolean
}

const getAlphabeticalMenu = (
  categories: IDisplayedCategory[],
  sortDirectionFactor: 1 | -1,
  languageCode: string,
): IDisplayedCategory[] => {
  const subCategoriesByFirstLetter: Record<string, ISubCategory[]> = {}
  
  categories
    .forEach(({ id, subcategories }) => {
      if (id === 'pdf-downloads') {
        return
      } 
      subcategories?.forEach((subcategory) => {
        const lowestLevelElements = subcategory.elements?.length 
          ? subcategory.elements 
          : [subcategory]
        lowestLevelElements.forEach((menuItem) => {
          const alphabeticalCategoryName = (
            sortDirectionFactor === 1 
              ? menuItem.alphabeticalCategoryNameAsc 
              : menuItem.alphabeticalCategoryNameDesc
            ) ?? menuItem.name[0].toLocaleUpperCase(languageCode)
          const subcategories = subCategoriesByFirstLetter[alphabeticalCategoryName] ?? []
          subCategoriesByFirstLetter[alphabeticalCategoryName] = subcategories
          subcategories.push(menuItem)
        })
      })
    })

  return Object.entries(subCategoriesByFirstLetter)
    .sort(([keyA], [keyB]) => compare(keyA, keyB, languageCode) * sortDirectionFactor)
    .map(([key, subcategories]) => ({
      id: key,
      name: key,
      hideOnAlphabetical: subcategories.every(({ 
        hideCategoryOnAlphabetical 
      }) => hideCategoryOnAlphabetical),
      subcategories: subcategories?.sort((a, b) => 
        compare(
          a.alphabeticalName || a.name, 
          b.alphabeticalName || b.name, 
          languageCode,
        ) * sortDirectionFactor
      ),
    }))
}

const translateOptional = <T extends string>(
  item: { [key in T]?: string }, 
  target: { [key in T]?: string }, 
  t: TFunction<'menu'>,
  field: T, 
) => {
  const content = item[field]
  if (content) {
    target[field] = t(content)
  }
}

const translateOptionals = <T extends string>(
  item: { [key in T]?: string }, 
  target: { [key in T]?: string }, 
  t: TFunction<'menu'>,
  fields: T[], 
) => {
  fields.forEach((field) => translateOptional(item, target, t, field))
}

const getTranslatedMenu = (
  t: TFunction<'menu'>,
): IDisplayedCategory[] =>
  menu.map((category) => ({
    ...category,
    name: t(category.name),
    subcategories: category.subcategories?.map((subcategory) => {
      const displayedSubcategory = {
        ...subcategory,
        name: t(subcategory.name),
        tag: subcategory.tag?.map((item) => t(item)),
        elements: subcategory.elements?.map((element) => {
          const displayedElement = {
            ...element,
            name: t(element.name),
            tag: element.tag?.map((item) => t(item)),
          }
          translateOptionals(element, displayedElement, t, [
            'alphabeticalCategoryNameAsc', 
            'alphabeticalCategoryNameDesc', 
            'alphabeticalName'
          ])
          return displayedElement
        })
      }
      translateOptionals(subcategory, displayedSubcategory, t, [
        'alphabeticalCategoryNameAsc', 
        'alphabeticalCategoryNameDesc', 
        'alphabeticalName'
      ])
      return displayedSubcategory
    })
  }))

const getMenuStructure = (
  translatedMenu: IDisplayedCategory[],
  search: string,
  directionFactor: DirectionFactor,
  languageCode: string,
): IDisplayedCategory[] => {
  const searched = search?.toLocaleUpperCase(languageCode)
  let filteredMenu: IDisplayedCategory[]
  if (searched) {
    filteredMenu = translatedMenu
      .map((category) => ({
        ...category,
        subcategories: category?.subcategories
          ?.map((subcategory) => ({
            ...subcategory,
            elements: subcategory?.elements
              ?.filter(({ name, alphabeticalName, tag }) =>
                name.toLocaleUpperCase(languageCode).includes(searched) ||
                  alphabeticalName?.toLocaleUpperCase(languageCode)?.includes(searched) ||
                  tag?.some((item) => areEquivalent(item, searched, languageCode))
              )
          }))
          ?.filter(({ name, alphabeticalName,elements, tag }) =>
            name.toLocaleUpperCase(languageCode).includes(searched) ||
              alphabeticalName?.toLocaleUpperCase(languageCode)?.includes(searched) ||
              elements?.length ||
              tag?.some((item) => areEquivalent(item, searched, languageCode)
          ))
      }))
      .filter(({ subcategories }) => subcategories?.length)
  } else {
    filteredMenu = translatedMenu
  }
  
  if (directionFactor === 0) {
    return filteredMenu
  }
  return getAlphabeticalMenu(filteredMenu, directionFactor, languageCode)
}

const getDirectionFactor = (
  isAlphabetical: boolean,
  sortDirection: SortDirection,
): DirectionFactor => {
  if (isAlphabetical) {
    return sortDirection === 'asc' ? 1 : -1
  }
  return 0
}

const useMenuStructure = (): IDisplayedCategory[] => {
  const search = useSearchQuery()
  const isAlphabetical = useIsAlphabetical()
  const sortDirection = useSortDirection()
  const { t } = useTranslation('menu')
  const { language } = useContext(I18nextContext)
  const directionFactor = getDirectionFactor(isAlphabetical, sortDirection)
  const translated = useMemo(() => getTranslatedMenu(t), [t])
  return useMemo(
    () => getMenuStructure(translated, search, directionFactor, language), 
    [translated, search, directionFactor, language]
  )
}

export default useMenuStructure
